import React, {useContext, useEffect, useState} from 'react';
import 'styles/item/Items.css';
import ItemMarketTile from './ItemMarketTile';
import MarketOptionsHeader from './header/MarketOptionsHeader';
import {useNavigate} from 'react-router-dom';
import {MarketContext} from 'util/market/MarketContext';
import itemTypeToEmoji from 'util/itemTypeToEmoji';
import UniversalEmptyPageHeader from "../universal/UniversalEmptyPageHeader";
import {useSessionConfiguration} from "../../util/SessionConfigurationContext";

const headerFitPx = 600;

const ListItem = ({type}) => {
    const [items, setItems] = useState([]);
    const [config, setConfig] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedSortField, setSelectedSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');
    const navigate = useNavigate();
    const {allItems} = useContext(MarketContext);
    const {isDesktopView} = useSessionConfiguration();

    // Effect to update items based on filters
    useEffect(() => {
        console.log('Triggering it! ' + sortOrder + ' | ' + selectedSortField);
        const defaultCompare = (a, b) => {
            const rarityA = itemTypeToEmoji.getRarityLevel(a.itemType);
            const rarityB = itemTypeToEmoji.getRarityLevel(b.itemType);

            if (type === 'item-store') {
                if (a.hasUserItem() !== b.hasUserItem()) {
                    return a.hasUserItem() ? 1 : -1;
                }
            }

            if (rarityA !== rarityB) {
                if (type === 'my-items') {
                    return (rarityA - rarityB) * -1;
                }
                return rarityA - rarityB;
            }

            const itemTypeA = a.itemType.toLowerCase();
            const itemTypeB = b.itemType.toLowerCase();
            if (itemTypeA < itemTypeB) return -1;
            if (itemTypeA > itemTypeB) return 1;
            return 0;
        };

        let newItems = genericOwnItemFilter(allItems, type);

        // Filter items based on search text
        if (searchText) {
            newItems = newItems.filter((item) =>
                itemTypeToEmoji[item.itemType].translation.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        // Apply sorting
        if (selectedSortField) {
            // Sort by selected field
            newItems.sort((a, b) => {
                const valueA = getFieldValue(a, selectedSortField);
                const valueB = getFieldValue(b, selectedSortField);

                if (valueA === valueB) {
                    return defaultCompare(a, b);
                }

                return sortOrder === 'desc' ? valueA - valueB : valueB - valueA;
            });
        } else {
            // Sort items alphabetically by default
            newItems.sort((a, b) => {
                return defaultCompare(a, b);
            });
        }

        setItems(newItems);
    }, [allItems, searchText, type, selectedSortField, sortOrder]);

    useEffect(() => {
        let newConfig = [];
        switch (type) {
            case 'my-items':
                newConfig = ['Alphabet', 'MyItems'];
                break;
            case 'item-store':
                newConfig = ['Alphabet', 'MyItems', 'AllSellOffers'];
                break;
            default:
                throw new Error(`Unknown type: ${type}`);
        }
        setConfig(newConfig);
    }, [type]);

    const handleItemClick = (item) => {
        navigate(`/items-detail/${item.itemType}/${type}`);
    };

    // Function to filter items based on filterTypeOwn
    const genericOwnItemFilter = (items, type) => {
        switch (type) {
            case 'item-store':
                return items;
            case 'my-items':
                return items.filter((item) => item.hasUserItem());
            default:
                throw new Error(`Unknown type: ${type}`);
        }
    };

    const toggleConfig = (label) => {
        setConfig((prevConfig) => {
            if (prevConfig.includes(label)) {
                return prevConfig.filter((item) => item !== label);
            } else {
                return [...prevConfig, label];
            }
        });
    };

    const handleSortChange = (field, order) => {
        setConfig((prevConfig) => {
            if (!prevConfig.includes(field)) {
                return [...prevConfig, field];
            }
            return prevConfig;
        });
        setSelectedSortField(field);
        setSortOrder(order);
    };


    // Define the getFieldValue function
    const getFieldValue = (item, field) => {
        switch (field) {
            case 'MyItems':
                return item.getUserQuantity();
            case 'AllItems':
                return item.getMarketQuantity();
            case 'AllBuyOffers':
                return item.getMarketBo();
            case 'AllSellOffers':
                return item.getMarketSo();
            case 'Alphabet':
                return itemTypeToEmoji[item.itemType].translation.toUpperCase();
            default:
                throw new Error(`Unknown field: ${field}`);
        }
    };

    return (
        <div>
            <UniversalEmptyPageHeader text={type === 'my-items' ? 'Inventory' : 'Store'}
                                      image={type === 'my-items' ? 'my-items' : 'store'}
                                      desktopHeaderScale={type === 'my-items' ? 0.95 : 0.92}>
                {isDesktopView && (
                    <MarketOptionsHeader
                        config={config}
                        toggleConfig={toggleConfig}
                        pageType={type}
                        onSearchTextChange={setSearchText}
                        onSortChange={handleSortChange}
                        selectedSortField={selectedSortField}
                        sortOrder={sortOrder}
                    />
                )}
            </UniversalEmptyPageHeader>
            {!isDesktopView && (
                <div className={'displaced-market-options-header'}>
                    <MarketOptionsHeader
                        config={config}
                        toggleConfig={toggleConfig}
                        pageType={type}
                        onSearchTextChange={setSearchText}
                        onSortChange={handleSortChange}
                        selectedSortField={selectedSortField}
                        sortOrder={sortOrder}
                    />
                </div>
            )}
            <div className="items-grid main-content-body-padding">
                {items.length > 0 ? (
                    items.map((item) => (
                        <ItemMarketTile
                            handleItemClick={handleItemClick}
                            item={item}
                            config={config}
                            key={item.itemType}
                        />
                    ))
                ) : (
                    <div className='empty-page-text'>
                        {(!searchText && type === 'my-items')
                            ? 'Go to store or lootbox to get some items'
                            : 'No items match the selected criteria'}
                    </div>)}
            </div>
        </div>
    );
};

export default ListItem;